const SAO_PAULO_TIMEZONE = 'America/Sao_Paulo';

/**
 * Converte uma data/hora local (São Paulo) para UTC para salvar no Supabase
 */
export const toUTC = (dateTimeLocal: string | Date): string => {
  let localDate: Date;
  
  if (typeof dateTimeLocal === 'string') {
    // Se é string datetime-local (YYYY-MM-DDTHH:MM), precisa ser interpretada como horário de São Paulo
    localDate = new Date(dateTimeLocal);
    
    // Ajustar para o fuso horário de São Paulo (UTC-3)
    // Quando o usuário seleciona 09:00 em São Paulo, deve ser salvo como 12:00 UTC
    const offsetSaoPaulo = -3; // São Paulo é UTC-3
    const offsetLocal = localDate.getTimezoneOffset() / 60; // Offset atual do navegador em horas
    const adjustment = offsetSaoPaulo - (-offsetLocal); // Calcular ajuste necessário
    
    localDate.setHours(localDate.getHours() - adjustment);
  } else {
    localDate = dateTimeLocal;
  }
  
  return localDate.toISOString();
};

/**
 * Converte uma data UTC do Supabase para horário de São Paulo
 */
export const fromUTC = (utcDateTime: string): Date => {
  const utcDate = new Date(utcDateTime);
  
  // Criar uma nova data ajustada para São Paulo (UTC-3)
  const saoPauloDate = new Date(utcDate.getTime() - (3 * 60 * 60 * 1000));
  
  return saoPauloDate;
};

/**
 * Formata uma data UTC para exibição em horário de São Paulo
 */
export const formatToSaoPaulo = (utcDateTime: string, formatStr: string = 'dd/MM/yyyy HH:mm'): string => {
  const utcDate = new Date(utcDateTime);
  
  // Usar toLocaleString com timezone de São Paulo
  if (formatStr === 'dd/MM/yyyy HH:mm') {
    return utcDate.toLocaleString('pt-BR', { 
      timeZone: SAO_PAULO_TIMEZONE,
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } else if (formatStr === 'dd/MM/yyyy') {
    return utcDate.toLocaleDateString('pt-BR', { 
      timeZone: SAO_PAULO_TIMEZONE,
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  } else if (formatStr === 'HH:mm') {
    return utcDate.toLocaleTimeString('pt-BR', { 
      timeZone: SAO_PAULO_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  // Fallback para formatos não suportados
  return utcDate.toLocaleString('pt-BR', { timeZone: SAO_PAULO_TIMEZONE });
};

/**
 * Converte data UTC para string no formato datetime-local (para inputs)
 */
export const toDateTimeLocal = (utcDateTime: string): string => {
  const utcDate = new Date(utcDateTime);
  
  // Ajustar para São Paulo manualmente
  const saoPauloTime = new Date(utcDate.getTime() - (3 * 60 * 60 * 1000));
  
  const year = saoPauloTime.getFullYear();
  const month = String(saoPauloTime.getMonth() + 1).padStart(2, '0');
  const day = String(saoPauloTime.getDate()).padStart(2, '0');
  const hours = String(saoPauloTime.getHours()).padStart(2, '0');
  const minutes = String(saoPauloTime.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Formata apenas o horário (HH:mm) em São Paulo
 */
export const formatTimeToSaoPaulo = (utcDateTime: string): string => {
  return formatToSaoPaulo(utcDateTime, 'HH:mm');
};

/**
 * Verifica se uma data está no mesmo dia (em São Paulo)
 */
export const isSameDayInSaoPaulo = (utcDateTime: string, compareDate: Date): boolean => {
  const utcDate = new Date(utcDateTime);
  
  // Converter ambas as datas para São Paulo
  const saoPauloDate = new Date(utcDate.toLocaleString('en-US', { timeZone: SAO_PAULO_TIMEZONE }));
  const compareSaoPaulo = new Date(compareDate.toLocaleString('en-US', { timeZone: SAO_PAULO_TIMEZONE }));
  
  return saoPauloDate.getDate() === compareSaoPaulo.getDate() &&
         saoPauloDate.getMonth() === compareSaoPaulo.getMonth() &&
         saoPauloDate.getFullYear() === compareSaoPaulo.getFullYear();
};

/**
 * Obtém a data atual em São Paulo
 */
export const nowInSaoPaulo = (): Date => {
  const now = new Date();
  return new Date(now.toLocaleString('en-US', { timeZone: SAO_PAULO_TIMEZONE }));
};